/**
 * @function Toast
 * @description Component for displaying a toast message.
 * @returns {JSX.Element | null} - The Toast component.
 */
import { Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/20/solid'
import { Fragment } from 'react'


/**
 * @interface ToastProps
 * @description Props for the Toast component.
 * @property {JSX.Element} icon - The icon to display.
 * @property {string} title - The title of the toast.
 * @property {boolean} isOpen - Whether the toast is open.
 * @property {string} description - The description of the toast.
 * @property {boolean} canClose - Whether the toast can be closed.
 * @property {() => void} onClose - The function to call when the toast is closed.
 */
interface ToastProps {
    icon: JSX.Element
    title: string
    isOpen: boolean
    description?: string
    canClose?: boolean
    onClose: () => void
}

/**
 * @function Toast
 * @description Component for displaying a toast message.
 * @returns {JSX.Element | null} - The Toast component.
 */
export function Toast({ isOpen, icon, title, canClose = true, onClose }: ToastProps) {
    return (
        <Transition
            show={isOpen}
            as={Fragment}
            enter="transform ease-out duration-300 transition"
            enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
            enterTo="translate-y-0 opacity-100 sm:translate-x-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
                <div className="p-4">
                    <div className="flex items-start justify-center">
                        <div className="flex-shrink-0">{icon}</div>
                        <div className="ml-3 w-0 flex-1">
                            <p className="text-sm font-medium text-gray-900">{title}</p>
                        </div>
                        {canClose && (
                            <div className="ml-4 flex flex-shrink-0">
                                <button
                                    type="button"
                                    className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                    onClick={onClose}
                                >
                                    <span className="sr-only">Close</span>
                                    <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Transition>
    )
}
